import { autoAttributesPlugin } from "@growthbook/growthbook/plugins";
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import { PropsWithChildren, useEffect, useState } from "react";
import { logger } from "@caju-beneficios/monitoring";

function getOrCreateDeviceId(): string {
  const existingDeviceId = Cookies.get("gb-deviceId");

  if (existingDeviceId) return existingDeviceId;

  const deviceId = uuidv4();

  Cookies.set("gb-deviceId", deviceId, {
    expires: 7,
    domain: window.location.hostname.endsWith("caju.com.br")
      ? ".caju.com.br"
      : undefined,
    secure: window.location.protocol === "https:",
  });

  return deviceId;
}

export function FeatureFlagProvider(props: PropsWithChildren) {
  const [deviceId] = useState(getOrCreateDeviceId);

  const [featureFlagClient] = useState(
    () =>
      new GrowthBook({
        apiHost: "https://cdn.growthbook.io",
        clientKey: process.env.REACT_APP_GROWTH_BOOK_CLIENT_KEY,
        enableDevMode: process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION",
        // Seeds the plugin's own id with our gb-deviceId cookie so its URL-change polling reasserts it instead of drifting to its own gbuuid cookie.
        plugins: [autoAttributesPlugin({ uuid: deviceId, uuidKey: "id" })],
        trackingCallback: (experiment, result) => {
          logger.info({
            message: "Experiment Viewed",
            context: {
              experimentId: experiment.key,
              variationId: result.key,
            },
          });
        },
      }),
  );

  useEffect(() => {
    async function init() {
      try {
        const response = await featureFlagClient.init();

        featureFlagClient.updateAttributes({ id: deviceId, deviceId });

        if (!response.success) throw response.error;
      } catch (error) {
        logger.error({
          message: "FeatureFlagProvider initialization failed",
          error: error as Error,
          context: {
            application: "quero-caju",
            component: "FeatureFlagProvider",
            service: "growthbook",
          },
        });
      }
    }

    init();
  }, [featureFlagClient, deviceId]);

  return (
    <GrowthBookProvider growthbook={featureFlagClient}>
      {props.children}
    </GrowthBookProvider>
  );
}
